import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { getWorkThumbnailUrl, queueThumbnail } from '../utils/workImages';

const PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
const retryStyle = { border: 0, background: 'transparent', color: 'inherit', font: 'inherit', fontSize: 12, fontWeight: 400, cursor: 'pointer', padding: '8px 10px' };

const WorkThumbnail = forwardRef(({ src, alt, style, onLoad, onError, onClick, loading, ...props }, forwardedRef) => {
  const element = useRef(null);
  const finish = useRef(null);
  const events = useRef({ onLoad, onError });
  events.current = { onLoad, onError };
  const [visible, setVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [state, setState] = useState('idle');
  const url = useMemo(() => getWorkThumbnailUrl(src), [src]);

  useEffect(() => {
    if (!element.current) return undefined;
    if (!window.IntersectionObserver) { setVisible(true); return undefined; }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '240px' });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [url]);

  useEffect(() => {
    if (!visible || !url) return undefined;
    let closed = false, timeout, retry;
    setDisplayed('');
    setState('queued');
    const cancel = queueThumbnail(release => {
      if (closed) { release(); return; }
      let settled = false;
      finish.current = (success, event) => {
        if (closed || settled) return;
        settled = true;
        clearTimeout(timeout);
        if (!success) setDisplayed('');
        setState(success ? 'loaded' : 'error');
        release();
        if (success) events.current.onLoad?.(event);
        else {
          events.current.onError?.(event);
          // One staggered automatic retry; further attempts require a user click.
          if (attempt === 0) retry = setTimeout(() => setAttempt(value => value + 1), 1500 + Math.random() * 1000);
        }
      };
      setState('loading');
      setDisplayed(url);
      timeout = setTimeout(() => finish.current?.(false), 12000);
    });
    return () => { closed = true; clearTimeout(timeout); clearTimeout(retry); finish.current = null; cancel(); };
  }, [url, visible, attempt]);

  return <>
    <img {...props} alt={alt || '作品'} ref={node => {
      element.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    }} key={`${url}:${attempt}`} src={displayed || PLACEHOLDER}
      data-work-thumbnail="true" data-image-state={state} data-original-src={src}
      style={{ ...style, ...(!displayed ? { minHeight: 160 } : {}) }}
      loading="eager" decoding="async" onClick={onClick}
      onLoad={event => { if (displayed) finish.current?.(true, event); }}
      onError={event => { if (displayed) finish.current?.(false, event); }} />
    {state === 'error' && <div style={{ textAlign: 'center', width: '100%' }}>
      <button type="button" style={retryStyle} onClick={event => { event.stopPropagation(); setAttempt(value => value + 1); }}>图片未加载，点击重试</button>
      {onClick && <button type="button" style={retryStyle} onClick={onClick}>查看原图</button>}
    </div>}
  </>;
});

export default WorkThumbnail;
